import { useState, useEffect } from "react";
import { useLang } from "../contexts/LanguageContext";
import { PRODUCTS } from "../data/products";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const INITIAL = {
  company: "",
  contact_name: "",
  email: "",
  phone: "",
  country: "",
  destination_port: "",
  product: "",
  quantity_mt: "",
  incoterm: "",
  message: "",
};

export function ContactSection({ preselectProduct }) {
  const { lang, t } = useLang();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselectProduct) {
      setForm((f) => ({ ...f, product: preselectProduct }));
    }
  }, [preselectProduct]);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity_mt: parseFloat(form.quantity_mt),
        phone: form.phone || null,
        destination_port: form.destination_port || null,
        incoterm: form.incoterm || null,
        message: form.message || null,
      };
      await api.post("/quotes", payload);
      toast.success(t.contact.success_title, {
        description: t.contact.success_desc,
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error(t.contact.error_title, {
        description:
          formatApiErrorDetail(err.response?.data?.detail) ||
          t.contact.error_desc,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border-b border-border bg-transparent px-0 py-3 focus:outline-none focus:border-primary transition-colors rounded-none text-foreground placeholder:text-muted-foreground";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-accent/40 border-t border-border"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-6">
          <p className="tracking-eyebrow uppercase text-xs text-secondary">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-balance">
            {t.contact.title}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
            {t.contact.lead}
          </p>
        </div>

        <form
          data-testid="quote-form"
          onSubmit={onSubmit}
          className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-10 md:gap-y-8"
        >
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.company}
            </label>
            <input
              data-testid="quote-input-company"
              required
              value={form.company}
              onChange={update("company")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.contact_name}
            </label>
            <input
              data-testid="quote-input-contact-name"
              required
              value={form.contact_name}
              onChange={update("contact_name")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.email}
            </label>
            <input
              data-testid="quote-input-email"
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.phone}
            </label>
            <input
              data-testid="quote-input-phone"
              value={form.phone}
              onChange={update("phone")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.country}
            </label>
            <input
              data-testid="quote-input-country"
              required
              value={form.country}
              onChange={update("country")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.destination_port}
            </label>
            <input
              data-testid="quote-input-port"
              value={form.destination_port}
              onChange={update("destination_port")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.product}
            </label>
            <select
              data-testid="quote-input-product"
              required
              value={form.product}
              onChange={update("product")}
              className={inputCls}
            >
              <option value="" disabled>
                {t.contact.select_product}
              </option>
              {PRODUCTS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.titles[lang]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.quantity}
            </label>
            <input
              data-testid="quote-input-quantity"
              required
              type="number"
              min="1"
              step="any"
              value={form.quantity_mt}
              onChange={update("quantity_mt")}
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.incoterm}
            </label>
            <input
              data-testid="quote-input-incoterm"
              placeholder="CIF / FOB / CFR ..."
              value={form.incoterm}
              onChange={update("incoterm")}
              className={inputCls}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] tracking-eyebrow uppercase text-muted-foreground">
              {t.contact.message}
            </label>
            <textarea
              data-testid="quote-input-message"
              rows={4}
              value={form.message}
              onChange={update("message")}
              className={`${inputCls} resize-none`}
            />
          </div>
          <div className="md:col-span-2 flex justify-end pt-4">
            <button
              data-testid="quote-submit-button"
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground px-10 py-4 text-sm tracking-wide hover:bg-[#2B1E17] transition-colors disabled:opacity-60"
            >
              {submitting ? t.contact.sending : t.contact.submit}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
